/**
 * GenerationNoiseSourcesEditor — multi-source acoustic inventory for the
 * Impacts stage (grid-screening-v2 input surface).
 *
 * Rows edit `input.noiseSources` directly: kind (sound power dBA re 1 pW vs
 * measured SPL @ reference distance), level, count. The workbench keeps the
 * legacy single-field screen when `noiseSources` is undefined; "Upgrade to
 * multi-source model" seeds the platform's 85 dBA @ 10 ft planning aggregate
 * as the first row. Catalog-synthesized rows (id 'equipment-acoustics')
 * surface their provenance and, for screening-reference values, the
 * mandatory "not an OEM guarantee" label.
 *
 * Emitter heights use the engine default (1.5 m acoustic centre); positions
 * are auto-synced from the test-fit by the workbench (noiseGeometry).
 */
import React from 'react';
import { Plus, Trash2, Volume2 } from 'lucide-react';

import { EQUIPMENT_ACOUSTIC_SOURCE_ID } from '../../../lib/generationImpact/generationEquipmentCatalog';
import type { GenerationNoiseUnit, NoiseDatum } from '../../../types/generationImpact';

const TEN_FEET_M = 3.048;

export interface GenerationNoiseSourcesEditorProps {
  noiseSources: GenerationNoiseUnit[] | undefined;
  readOnly: boolean;
  /** Basis of a catalog-synthesized acoustic row, for honest labeling. */
  equipmentAcousticsBasis?: 'screening-reference' | 'oem-guarantee' | null;
  onChange: (sources: GenerationNoiseUnit[]) => void;
}

const PLANNING_SEED: GenerationNoiseUnit = {
  id: 'planning-aggregate',
  name: 'Aggregate generation source (planning assumption)',
  count: 1,
  noise: {
    kind: 'reference-spl',
    levelDbA: 85,
    referenceDistanceM: TEN_FEET_M,
    scope: 'aggregate',
    basisLabel: 'User-supplied planning assumption: 85 dBA at 10 ft',
  },
};

function datumOf(unit: GenerationNoiseUnit): NoiseDatum {
  if (unit.noise) return unit.noise;
  return {
    kind: 'sound-power',
    levelDbA: unit.soundPowerDbA ?? 100,
    ...(unit.directivityCorrectionDb !== undefined
      ? { directivityCorrectionDb: unit.directivityCorrectionDb }
      : {}),
  };
}

export function GenerationNoiseSourcesEditor({
  noiseSources,
  readOnly,
  equipmentAcousticsBasis,
  onChange,
}: GenerationNoiseSourcesEditorProps): React.JSX.Element {
  const rows = noiseSources ?? [];

  const patchRow = (index: number, patch: Partial<GenerationNoiseUnit>): void => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const patchDatum = (index: number, patch: Partial<NoiseDatum>): void => {
    const current = datumOf(rows[index]);
    patchRow(index, { noise: { ...current, ...patch } as NoiseDatum });
  };

  if (noiseSources === undefined) {
    return (
      <div className="border border-dashed border-border p-3 rounded-lg" data-testid="noise-sources-upgrade">
        <p className="mb-2 text-xs text-text-secondary">
          The single-source screen is active. Upgrade to the multi-source model to
          place equipment-specific decibel sources on the map, draw sound
          barriers, and compute the noise dissipation field.
        </p>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => onChange([structuredClone(PLANNING_SEED)])}
          className="inline-flex items-center gap-1 border border-primary bg-primary/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-primary disabled:opacity-40 rounded-md"
          data-testid="noise-sources-upgrade-button"
        >
          <Volume2 className="h-3 w-3" aria-hidden />
          Upgrade to multi-source model
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="noise-sources-editor">
      {rows.map((row, index) => {
        const datum = datumOf(row);
        const isEquipmentRow = row.id === EQUIPMENT_ACOUSTIC_SOURCE_ID;
        return (
          <div key={row.id} className="border border-border p-2 rounded-md" data-testid={`noise-source-row-${row.id}`}>
            <div className="mb-1 flex items-center gap-2">
              <input
                type="text"
                value={row.name}
                disabled={readOnly}
                onChange={(event) => patchRow(index, { name: event.target.value })}
                className="min-w-0 flex-1 border border-border bg-background px-2 py-1 text-xs text-text-primary rounded-md"
                aria-label={`Source ${index + 1} name`}
                data-testid={`noise-source-name-${row.id}`}
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => onChange(rows.filter((_, i) => i !== index))}
                  className="shrink-0 text-text-muted hover:text-red-500"
                  aria-label={`Delete ${row.name}`}
                  data-testid={`noise-source-delete-${row.id}`}
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                </button>
              )}
            </div>
            {isEquipmentRow && equipmentAcousticsBasis === 'screening-reference' && (
              <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-amber-500" data-testid="noise-source-screening-label">
                Screening default — not an OEM guarantee
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-wider text-text-muted">
                Basis
                <select
                  value={datum.kind}
                  disabled={readOnly}
                  onChange={(event) => {
                    const kind = event.target.value as NoiseDatum['kind'];
                    patchRow(index, {
                      noise: kind === 'sound-power'
                        ? { kind, levelDbA: datum.levelDbA }
                        : { kind, levelDbA: datum.levelDbA, referenceDistanceM: TEN_FEET_M, scope: 'per-unit' },
                    });
                  }}
                  className="border border-border bg-background px-1 py-1 text-xs text-text-primary rounded-md"
                  data-testid={`noise-source-kind-${row.id}`}
                >
                  <option value="sound-power">Sound power (dBA re 1 pW)</option>
                  <option value="reference-spl">Measured SPL @ distance</option>
                </select>
              </label>
              <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-wider text-text-muted">
                Level (dBA)
                <input
                  type="number"
                  value={datum.levelDbA}
                  disabled={readOnly}
                  onChange={(event) => patchDatum(index, { levelDbA: Number(event.target.value) })}
                  className="border border-border bg-background px-2 py-1 text-xs text-text-primary rounded-md"
                  data-testid={`noise-source-level-${row.id}`}
                />
              </label>
              {datum.kind === 'reference-spl' && (
                <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-wider text-text-muted">
                  Ref distance (m)
                  <input
                    type="number"
                    min={0.01}
                    step={0.1}
                    value={datum.referenceDistanceM}
                    disabled={readOnly}
                    onChange={(event) => patchDatum(index, { referenceDistanceM: Number(event.target.value) })}
                    className="border border-border bg-background px-2 py-1 text-xs text-text-primary rounded-md"
                    data-testid={`noise-source-refdist-${row.id}`}
                  />
                </label>
              )}
              {datum.kind === 'reference-spl' && (
                <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-wider text-text-muted">
                  Scope
                  <select
                    value={datum.scope}
                    disabled={readOnly}
                    onChange={(event) => patchDatum(index, { scope: event.target.value as 'aggregate' | 'per-unit' })}
                    className="border border-border bg-background px-1 py-1 text-xs text-text-primary rounded-md"
                    data-testid={`noise-source-scope-${row.id}`}
                  >
                    <option value="per-unit">Per unit (count multiplies)</option>
                    <option value="aggregate">Aggregate (count ignored)</option>
                  </select>
                </label>
              )}
              <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-wider text-text-muted">
                Count
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={row.count}
                  disabled={readOnly}
                  onChange={(event) => patchRow(index, { count: Math.max(0, Math.trunc(Number(event.target.value))) })}
                  className="border border-border bg-background px-2 py-1 text-xs text-text-primary rounded-md"
                  data-testid={`noise-source-count-${row.id}`}
                />
              </label>
            </div>
          </div>
        );
      })}
      {!readOnly && (
        <button
          type="button"
          onClick={() => onChange([
            ...rows,
            {
              id: `noise-source-${crypto.randomUUID()}`,
              name: `Source ${rows.length + 1}`,
              count: 1,
              noise: { kind: 'sound-power', levelDbA: 100 },
            },
          ])}
          className="inline-flex items-center gap-1 border border-border px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-text-secondary rounded-md"
          data-testid="noise-source-add"
        >
          <Plus className="h-3 w-3" aria-hidden />
          Add source
        </button>
      )}
      <p className="text-[9px] text-text-muted">
        Emitter heights default to the 1.5 m acoustic centre; positions follow
        the placed generation blocks automatically. Screening-grade single-band
        model — not ISO 9613-2 compliance.
      </p>
    </div>
  );
}
